import {
  Coding,
  Encounter,
  Questionnaire,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { PhysicalExamDisplays } from "@/fhir/enums/physical-exams";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { getExtensionValues } from "@/utils/medplum";

import { PhysicalExamSectionAnswer } from "../types/physical-exams";
import {
  PhysicalExamForm,
  PhysicalExamSectionField,
  SingleChoiceField,
} from "../zod-schemas/physical-exams";

export function getActivePhysicalExamsURLs(encounter: Encounter) {
  return (getExtensionValues(encounter, Extensions.EncounterPhysicalExam) ?? []) as string[];
}

export function createPhysicalExamInitialValues<T extends PhysicalExamForm>(
  questionnaireResponse: QuestionnaireResponse | undefined,
  values: T,
): T {
  if (!questionnaireResponse) return values;

  const initialValues: T = {
    ...values,
    sections: { ...values.sections },
    notes: getNoteAnswer(questionnaireResponse),
  };
  const sections = questionnaireResponse.item ?? [];

  for (const section of sections) {
    const sectionData = initialValues.sections[section.linkId];

    if (sectionData && isSingleChoiceSection(sectionData)) {
      // Type-safe update for SingleChoice sections
      (initialValues.sections[section.linkId] as SingleChoiceField) = {
        ...sectionData,
        singleChoice: getSectionItemBooleanAnswer(section, "SingleChoice"),
        declined: getSectionItemBooleanAnswer(section, "Declined"),
        singleChoiceDetails: getSectionItemCodingAnswer(section, "SingleChoiceDetails"),
      };
    } else if (sectionData && isPhysicalExamSection(sectionData)) {
      // Type-safe update for PhysicalExam sections
      (initialValues.sections[section.linkId] as PhysicalExamSectionField & { notes?: string }) = {
        ...sectionData,
        normal: getSectionItemBooleanAnswer(section, "Normal"),
        notNormal: getSectionItemBooleanAnswer(section, "NotNormal"),
        declined: getSectionItemBooleanAnswer(section, "Declined"),
        normalDetails: getSectionItemCodingAnswer(section, "NormalDetails"),
        notNormalDetails: getSectionItemCodingAnswer(section, "NotNormalDetails"),
        notes: getSectionItemStringAnswer(section, "Notes"),
      };
    }
  }
  return initialValues;
}

function findItemInSection(section: QuestionnaireResponseItem, linkId: string) {
  return section.item?.find((item) => item.linkId === `${section.linkId}${linkId}`);
}

function getSectionItemBooleanAnswer(section: QuestionnaireResponseItem, linkId: string) {
  const item = findItemInSection(section, linkId);
  return item?.answer?.[0]?.valueBoolean ?? false;
}

function getSectionItemCodingAnswer(section: QuestionnaireResponseItem, linkId: string) {
  const item = findItemInSection(section, linkId);
  return item?.answer?.map((item) => item.valueCoding?.code ?? "")?.filter(Boolean) ?? [];
}

function getSectionItemStringAnswer(section: QuestionnaireResponseItem, linkId: string) {
  const item = findItemInSection(section, linkId);
  return item?.answer?.[0]?.valueString ?? "";
}

function getNoteAnswer(questionnaireResponse: QuestionnaireResponse) {
  return (
    questionnaireResponse.item?.find((item) => item.linkId === "notes")?.answer?.[0]?.valueString ??
    ""
  );
}

export function createPhysicalExamSectionQuestionnaireResponseItem<T extends PhysicalExamForm>(
  sectionId: string,
  section: T["sections"][string] & { notes?: string },
  questionnaire: Questionnaire,
): QuestionnaireResponseItem | undefined {
  if ("singleChoice" in section) {
    const questionnaireItem = questionnaire.item?.find((item) => item.linkId === sectionId);
    if (!questionnaireItem) return undefined;

    const sectionItem: QuestionnaireResponseItem = {
      linkId: sectionId,
      text: questionnaireItem.text,
      extension: questionnaireItem.extension,
      item: [
        {
          linkId: `${sectionId}Declined`,
          answer: [{ valueBoolean: section.declined }],
        },
      ],
    };

    if (section.declined) {
      sectionItem.item?.push({
        linkId: `${sectionId}SingleChoice`,
        answer: [{ valueBoolean: false }],
      });
      return sectionItem;
    }

    sectionItem.item?.push({
      linkId: `${sectionId}SingleChoice`,
      answer: [{ valueBoolean: section.singleChoice }],
    });

    if (section.singleChoiceDetails.length > 0) {
      sectionItem.item?.push(
        getPECodeableConceptSingleChoiceAnswer(
          `${sectionId}SingleChoiceDetails`,
          section.singleChoiceDetails,
        ),
      );
    }
    return sectionItem;
  }

  const sectionField = section as PhysicalExamSectionField & { notes?: string };
  const questionnaireItem = questionnaire.item?.find((item) => item.linkId === sectionId);
  if (!questionnaireItem) return undefined;

  const sectionItem: QuestionnaireResponseItem = {
    linkId: sectionId,
    text: questionnaireItem.text,
    extension: questionnaireItem.extension,
    item: [
      {
        linkId: `${sectionId}Declined`,
        answer: [{ valueBoolean: sectionField.declined }],
      },
    ],
  };

  if (sectionField.declined) {
    sectionItem.item?.push(
      {
        linkId: `${sectionId}Normal`,
        answer: [{ valueBoolean: false }],
      },
      {
        linkId: `${sectionId}NotNormal`,
        answer: [{ valueBoolean: false }],
      },
    );
    return sectionItem;
  }

  if (!sectionField.normal && !sectionField.notNormal) {
    return undefined;
  }

  sectionItem.item?.push(
    {
      linkId: `${sectionId}Normal`,
      answer: [{ valueBoolean: sectionField.normal }],
    },
    {
      linkId: `${sectionId}NotNormal`,
      answer: [{ valueBoolean: sectionField.notNormal }],
    },
  );

  if (sectionField.normal && sectionField.normalDetails.length > 0) {
    sectionItem.item?.push(
      getPECodeableConceptAnswer(`${sectionId}NormalDetails`, sectionField.normalDetails),
    );
  }

  if (sectionField.notNormal && sectionField.notNormalDetails.length > 0) {
    sectionItem.item?.push(
      getPECodeableConceptAnswer(`${sectionId}NotNormalDetails`, sectionField.notNormalDetails),
    );
  }

  if (sectionField.notes && sectionField.notes.trim()) {
    sectionItem.item?.push({
      linkId: `${sectionId}Notes`,
      answer: [{ valueString: sectionField.notes.trim() }],
    });
  }

  return sectionItem;
}

function getPECodeableConceptAnswer(
  linkId: string,
  items: PhysicalExamSectionField["normalDetails"] | PhysicalExamSectionField["notNormalDetails"],
) {
  return {
    linkId,
    answer: items.map((code) => ({
      valueCoding: {
        system: QuiltedHealthCodeSystems.PhysicalExamFindings,
        code,
        display: PhysicalExamDisplays[code as keyof typeof PhysicalExamDisplays],
      } as Coding,
    })),
  };
}

function getPECodeableConceptSingleChoiceAnswer(
  linkId: string,
  items: SingleChoiceField["singleChoiceDetails"],
) {
  return {
    linkId,
    answer: items.map((code) => ({
      valueCoding: {
        system: QuiltedHealthCodeSystems.PhysicalExamFindings,
        code,
        display: PhysicalExamDisplays[code as keyof typeof PhysicalExamDisplays],
      } as Coding,
    })),
  };
}

export function getSectionAnswers(
  questionnaireResponse?: QuestionnaireResponse,
): PhysicalExamSectionAnswer[] {
  if (!questionnaireResponse?.item?.[0]) return [];

  return questionnaireResponse.item.map((section): PhysicalExamSectionAnswer => {
    const find = (linkId: string) => section.item?.find((item) => item.linkId === linkId);

    const getCoding = (linkId: string) =>
      (find(linkId)
        ?.answer?.map((answer) => answer.valueCoding?.display || answer.valueCoding?.code)
        ?.filter(Boolean) as string[]) || [];

    if (section.extension?.[0].valueString === "single") {
      return {
        sectionName: section.text || "",
        type: "section",
        items: {
          declined: find(`${section.linkId}Declined`)?.answer?.[0]?.valueBoolean ?? false,
          singleChoice: find(`${section.linkId}SingleChoice`)?.answer?.[0]?.valueBoolean ?? false,
          singleChoiceDetails: getCoding(`${section.linkId}SingleChoiceDetails`),
        },
      };
    } else if (section.linkId !== "notes") {
      return {
        sectionName: section.text || "",
        type: "section",
        items: {
          normal: find(`${section.linkId}Normal`)?.answer?.[0]?.valueBoolean ?? false,
          notNormal: find(`${section.linkId}NotNormal`)?.answer?.[0]?.valueBoolean ?? false,
          normalDetails: getCoding(`${section.linkId}NormalDetails`),
          notNormalDetails: getCoding(`${section.linkId}NotNormalDetails`),
          declined: find(`${section.linkId}Declined`)?.answer?.[0]?.valueBoolean ?? false,
        },
      };
    } else {
      return {
        type: "notes",
        notes: section.answer?.[0]?.valueString,
      };
    }
  });
}

// Type guard to identify SingleChoice section
export const isSingleChoiceSection = (
  data: PhysicalExamSectionField | SingleChoiceField | undefined,
): data is SingleChoiceField => {
  return data !== undefined && "singleChoice" in data;
};

// Type guard to identify PhysicalExamSection section
export const isPhysicalExamSection = (
  data: PhysicalExamSectionField | SingleChoiceField | undefined,
): data is PhysicalExamSectionField => {
  return data !== undefined && "normal" in data && "notNormal" in data;
};

// Type-safe extraction of values

export const getCurrentFormValue = (
  sectionData: PhysicalExamSectionField | SingleChoiceField | undefined,
  type: "singleChoice" | "normal" | "notNormal" | "declined",
): boolean | undefined => {
  if (!sectionData || type === "declined") return undefined;

  switch (type) {
    case "singleChoice":
      if (isSingleChoiceSection(sectionData)) {
        return sectionData.singleChoice;
      }
      break;
    case "normal":
      if (isPhysicalExamSection(sectionData)) {
        return sectionData.normal;
      }
      break;
    case "notNormal":
      if (isPhysicalExamSection(sectionData)) {
        return sectionData.notNormal;
      }
      break;
  }
  return undefined;
};

export const getDetailsValues = (
  sectionData: PhysicalExamSectionField | SingleChoiceField | undefined,
  type: "singleChoice" | "normal" | "notNormal" | "declined",
): string[] => {
  if (!sectionData || type === "declined") return [];

  switch (type) {
    case "singleChoice":
      if (isSingleChoiceSection(sectionData)) {
        return sectionData.singleChoiceDetails || [];
      }
      break;
    case "normal":
      if (isPhysicalExamSection(sectionData)) {
        return sectionData.normalDetails || [];
      }
      break;
    case "notNormal":
      if (isPhysicalExamSection(sectionData)) {
        return sectionData.notNormalDetails || [];
      }
      break;
  }

  return [];
};
